import React, { FC, useCallback, useEffect, useState } from 'react'
import { Loader, Stack, Typography } from '@papercutsoftware/pcds-react'
import { UserCredential } from 'firebase/auth'
import { IdentityProvider } from '@/api/identityProviders'
import { getFirebaseUiConfig } from '@/utils/firebase/firebaseUiConfig'
import { clearLoggedInUserState, translateFirebaseError } from '@/utils/firebase/firebaseAuth'
import { StyledFirebaseUi } from '@/styles/firebaseUi.styles'
import { ErrorCodes, errorIs, ErrorMessage, toMessage } from '@/utils/errorCodes'
import { renderError } from '@/components/RenderAlert/RenderErrorAlert'
import { init } from '@/components/firebase/ui/initializer'
import { diagnoseFederatedAuthFailure } from '@/utils/firebase/googleApiDiagnostics'

type FirebaseUiAuthProps = {
  tenantId: string
  providers: IdentityProvider[]
  onAuthSuccess: (idToken: string) => void
  onAuthFailure: (err: unknown) => void
  onRedirectStatusChange?: (redirecting: boolean) => void
  setAuthError: (errMsg: ErrorMessage) => void
  containerId: string
}

// A re-implementation of `react-firebaseui` which we cannot use as it only supports up to react 17 and the repository doesn't appear to be maintained for quite some time.
const FirebaseUiAuth: FC<FirebaseUiAuthProps> = ({
  tenantId,
  providers,
  onAuthSuccess,
  onAuthFailure,
  onRedirectStatusChange,
  setAuthError,
  containerId,
}) => {
  const [pendingRedirect, setPendingRedirect] = useState<boolean>(false)
  const [initError, setInitError] = useState<ErrorMessage | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ui, setUI] = useState<any>(null) // type firebaseui.auth.AuthUI
  const [isErrorCreatingFirebaseUI, setIsErrorCreatingFirebaseUI] = useState<boolean>(false)
  useEffect(() => {
    const initFirebaseUI = async () => {
      setIsErrorCreatingFirebaseUI(false)
      setUI(null)
      try {
        const firebaseUi = await init(tenantId)
        setUI(firebaseUi)
      } catch (e) {
        console.error('failed to create AuthUI', e)
        setIsErrorCreatingFirebaseUI(true)
      }
    }
    initFirebaseUI()
  }, [tenantId])

  const changeRedirectStatus = useCallback(
    (redirecting: boolean) => {
      setPendingRedirect(redirecting)
      if (onRedirectStatusChange) {
        onRedirectStatusChange(redirecting)
      }
    },
    [onRedirectStatusChange],
  )

  useEffect(() => {
    const intervalId = setInterval(() => {
      const firebaseErrorElement = document.querySelector('.firebaseui-info-bar-message')
      const firebaseErrorString = firebaseErrorElement?.childNodes[0]?.textContent?.trim()
      if (firebaseErrorString) {
        const firebaseUIErrorInfoBar = document.querySelector('.firebaseui-info-bar')
        console.error(
          `Failed to authenticate: ${firebaseErrorString}. Firebase error DOM element: ${firebaseUIErrorInfoBar?.outerHTML}`,
        )
        firebaseUIErrorInfoBar?.remove()
        const rawError = new Error(firebaseErrorString)
        const errorCode = translateFirebaseError(rawError)
        if (errorIs(errorCode, ErrorCodes.GenericAuthFailure)) {
          const errorMessage = new ErrorMessage()
          errorMessage.title = 'Something went wrong'
          errorMessage.content = firebaseErrorString
          setAuthError(errorMessage)
        } else {
          setAuthError(toMessage(errorCode))
        }
        onAuthFailure(rawError)
        changeRedirectStatus(false)

        // --- Google API access diagnostics. Delete this block along with `googleApiDiagnostics` to drop them. ---
        // `auth/internal-error` covers many unrelated causes, so replace the message above only once we know
        // Google API access is what actually failed. Deliberately not awaited, so the customer sees the original
        // error immediately rather than waiting on the checks.
        diagnoseFederatedAuthFailure(rawError).then((diagnosis) => {
          if (diagnosis) {
            setAuthError(toMessage(diagnosis))
          }
        })
        // --- end of Google API access diagnostics ---
      }
    }, 1000)

    return () => clearInterval(intervalId)
  })

  useEffect(() => {
    const startUi = async () => {
      try {
        const uiConfig = getFirebaseUiConfig(providers, async (authResult: UserCredential) => {
          try {
            const idTokenResult = await authResult.user.getIdTokenResult(false)
            await clearLoggedInUserState(tenantId) // we no longer need the logged in state on the client side as we don't use it beyond this point
            onAuthSuccess(idTokenResult.token)
          } catch (err) {
            onAuthFailure(err)
            changeRedirectStatus(false)
          }
        })
        changeRedirectStatus(ui.isPendingRedirect())
        await ui.start(`#${containerId}`, uiConfig)
      } catch (error) {
        const errorMessage = new ErrorMessage()
        errorMessage.content = 'Failed to start Firebase UI'
        setInitError(errorMessage)
      }
    }

    if (isErrorCreatingFirebaseUI) {
      const errorMessage = new ErrorMessage()
      errorMessage.content = 'Failed to initialize Firebase UI'

      setInitError(errorMessage)

      return
    }
    if (ui) {
      startUi()
    }
  }, [
    tenantId,
    providers,
    ui,
    isErrorCreatingFirebaseUI,
    onAuthSuccess,
    onAuthFailure,
    containerId,
    changeRedirectStatus,
  ])

  if (!ui) {
    return
  }

  return (
    <>
      {initError ? renderError(initError) : <></>}
      <StyledFirebaseUi>
        <div
          data-testid="firebase-ui-container"
          id={containerId}
          style={{
            display: pendingRedirect ? 'none' : 'block',
          }}
        />
      </StyledFirebaseUi>
      {pendingRedirect && (
        <>
          <Stack alignItems="center" direction="column">
            <Loader />
            <Typography>Authenticating</Typography>
          </Stack>
        </>
      )}
    </>
  )
}

export default FirebaseUiAuth
